// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and
// users) will only see deployed updates on subsequent visits to a page,
// after all the existing tabs open on the page have been closed, since
// previously cached resources are updated in the background.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('This web app is being served cache-first by a service worker.');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Covers a worker that was ALREADY sitting in .waiting from before this exact page load —
      // e.g. an earlier visibilitychange/interval check (below) already found one, then the user
      // reloaded the page (on purpose or by accident, not via UpdateAvailableBanner's own Refresh
      // button) before acting on it. onupdatefound below only fires when a worker is NEWLY
      // discovered during THIS registration — an already-known waiting worker never re-triggers
      // it, so without this check the banner has no way to reappear on the fresh page even though
      // the update is still genuinely sitting there ready to go. Confirmed live: this is exactly
      // why the banner "disappears and doesn't come back" after an accidental refresh.
      if (registration.waiting && config && config.onUpdate) {
        config.onUpdate(registration);
      }

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('New content is available and will be used when all tabs for this page are closed.');
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log('Content is cached for offline use.');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      // A browser only checks for a new service worker on its own roughly every 24h in the
      // background, or on an actual page navigation — an installed/home-screen PWA that's just
      // reopened (not force-quit and relaunched) can sit on a stale deploy far longer than that,
      // since there's no address-bar reload to trigger a check. Checking eagerly the moment the
      // app becomes visible again catches a deploy that happened while it was backgrounded much
      // sooner, feeding the exact same onupdatefound/onUpdate path above.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      });

      // The two triggers above both depend on the tab losing and regaining visibility (or a
      // fresh load) at some point — a tab left open and continuously focused (a dispatcher's
      // second monitor, an admin who tabs away to other apps rather than other browser tabs)
      // never fires either one, so it can sit on a stale deploy indefinitely even with the
      // no-cache header on service-worker.js itself doing its job correctly. This is very likely
      // why the update banner "doesn't always come up" — it was only ever checking at two
      // specific moments, not the passage of time itself. A 20-minute interval is frequent enough
      // that a deploy is noticed within one work session without polling so often it's wasteful
      // (the fetch itself is tiny — a few KB conditional GET — and skipped entirely if the tab
      // was already hidden and about to get the visibilitychange check anyway).
      setInterval(() => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      }, 20 * 60 * 1000);
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
